// src/hooks/useCustomerMap.ts
// 参考(ref/map)の customermap.js の CustomerMap オブジェクト（jQuery実装）を、
// Reactの状態・ロジックとして移植したもの。
//
// ■ 方針
//   ・API通信・状態管理・ページング/ソート/クエリー検索/選択履歴などの「機能」を移植。
//   ・Google Maps の描画（createIcon/selectIcon 等のDOM操作）は含めない
//     ＝「地図UIとの紐づけ」は後続で行う。将来の紐づけに必要なデータ（周辺顧客・
//       選択顧客・地図設定）とメソッドだけを提供する。
//
// ■ 参考メソッド対応
//   initAfterProc        → loadInit
//   selectAreaParamProc  → buildRepageParam
//   selectArea~AfterProc → searchByBounds
//   selectQuery          → selectQuery
//   sortList             → changeSort
//   changeListCount      → changeListCount
//   changeListPage       → changePage
//   selectInfo/After     → selectCustomer
//   saveHistory/clear    → saveHistory/clearHistory、goPrev/goNext

import { useCallback, useRef, useState } from 'react';
import { customerMapAPI } from '../services/customer-map-api';
import {
  CustomerMapBounds,
  CustomerMapInitResponse,
  CustomerMapItem,
  CustomerMapQuery,
  CustomerMapRank,
  CustomerMapRepageRequest,
  CustomerMapRepageResponse,
} from '../types/customerMap';

/** 地図設定（init で取得）。 */
export interface CustomerMapConfig {
  googleMapsApi: string;
  googleMapsApiKey: string;
  defaultLat: number;
  defaultLng: number;
  defaultZoomNone: number;
  defaultZoom: number;
  minZoom: number;
}

/** 選択中顧客（詳細）。 */
export interface CustomerMapSelected {
  customerno: number;
  companyname?: string;
  customername?: string;
  latitude?: number | null;
  longitude?: number | null;
  rankno?: number;
  rankname?: string;
  tel?: string;
  mobiletel?: string;
  address?: string;
}

/**
 * レスポンスが正常か。
 * 実APIは statuscode を持たず、業務エラーは errorMessageList に入る（RestApiResponseBaseDto）。
 * 通信失敗時はサービスが statuscode:400 を返す。
 */
const isOk = (res: any): boolean => {
  if (!res) return false;
  if (res.statuscode !== undefined && res.statuscode !== 200) return false;
  if (Array.isArray(res.errorMessageList) && res.errorMessageList.length > 0) return false;
  return true;
};

export function useCustomerMap() {
  // ---- 表示用の状態 ----
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<CustomerMapConfig | null>(null);

  // マスター類
  const [rankList, setRankList] = useState<CustomerMapRank[]>([]);
  const [queryList, setQueryList] = useState<CustomerMapQuery[]>([]);
  const [querySearchTypeList, setQuerySearchTypeList] = useState<Record<string, string>>({});
  const [listViewCountMap, setListViewCountMap] = useState<number[]>([]);

  // 一覧・周辺顧客
  const [customerList, setCustomerList] = useState<CustomerMapItem[]>([]);
  const [customerListCount, setCustomerListCount] = useState<number>(0);
  const [areaList, setAreaList] = useState<CustomerMapItem[]>([]);

  // 絞り込み・ページング・ソート
  const [page, setPage] = useState<number>(1);
  const [listViewCount, setListViewCount] = useState<number>(0);
  const [sortKey, setSortKey] = useState<number>(0);
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [selectedQueryNo, setSelectedQueryNo] = useState<number | null>(null);
  const [querySearching, setQuerySearching] = useState<boolean>(false);

  // 選択顧客
  const [selected, setSelected] = useState<CustomerMapSelected | null>(null);
  // 起点顧客（カルテの顧客）。選択が変わっても固定。
  const [home, setHome] = useState<CustomerMapSelected | null>(null);

  // 選択履歴（戻る/進む）
  const [canGoPrev, setCanGoPrev] = useState<boolean>(false);
  const [canGoNext, setCanGoNext] = useState<boolean>(false);

  // 地図ピンの描画リセット合図（参考: execShowModal/クエリ検索での icons/makers/area クリア）。
  // 値が変わると MapCanvas が既存ピンを全消去して描き直す。
  const [mapResetToken, setMapResetToken] = useState<number>(0);
  // リセット予約フラグ。トークンの更新は「新データ(areaList)が state に入るのと同じ
  // レンダー」で行う必要がある（await 前に上げると旧データのまま一度リセットが走り、
  // 旧ピンを再描画してしまうため）。loadInit/selectQuery で予約し、データ反映時に確定する。
  const pendingMapResetRef = useRef<boolean>(false);

  // 地図の表示位置（中心・ズーム）の記憶。戻るボタンでの復元時に、離脱時の画角を
  // そのまま再現するため MapCanvas から随時受け取って保持する。
  const viewRef = useRef<{ center: { lat: number; lng: number }; zoom: number } | null>(null);

  // ---- 内部参照（再レンダリングに依存せず最新値を使う） ----
  const boundsRef = useRef<CustomerMapBounds | null>(null);
  const selectedNoRef = useRef<number | null>(null);
  const rankListRef = useRef<CustomerMapRank[]>([]);
  const prevListRef = useRef<number[]>([]);
  const nextListRef = useRef<number[]>([]);
  // repage時に最新の絞り込み値を参照するためのミラー
  const filtersRef = useRef({
    page: 1,
    listViewCount: 0,
    sortKey: 0,
    sortAsc: false,
    queryNo: null as number | null,
  });

  // 地図の現在の表示位置を記憶（MapCanvas の bounds_changed から随時呼ばれる）。
  const rememberView = useCallback((center: { lat: number; lng: number }, zoom: number) => {
    viewRef.current = { center, zoom };
  }, []);
  // 記憶している表示位置を取り出す（復元表示の初期中心/ズームに使う）。
  const peekView = useCallback(() => viewRef.current, []);

  // rankno → rankname 解決（参考: rank.some(...)）
  const resolveRankName = useCallback((rankno?: number): string | undefined => {
    if (rankno == null) return undefined;
    return rankListRef.current.find((r) => r.rankno === rankno)?.rankname;
  }, []);

  // 履歴ボタンの活性状態を更新（参考: selectCustomerButtonCtrl）
  const refreshHistoryState = useCallback(() => {
    setCanGoPrev(prevListRef.current.length > 1);
    setCanGoNext(nextListRef.current.length > 0);
  }, []);

  // repageパラメータ生成（参考: selectAreaParamProc）
  const buildRepageParam = useCallback((): CustomerMapRepageRequest => {
    const b = boundsRef.current;
    const f = filtersRef.current;
    return {
      lat1: b?.lat1 ?? null,
      lat2: b?.lat2 ?? null,
      lng1: b?.lng1 ?? null,
      lng2: b?.lng2 ?? null,
      listPage: f.page,
      listViewCount: f.listViewCount,
      sortKey: f.sortKey,
      sortOrder: f.sortAsc,
      queryno: f.queryNo,
    };
  }, []);

  // repage結果を状態へ反映（参考: selectAreaAfterProc / selectQueryAfterProc / createList）
  const applyRepageResult = useCallback((res: CustomerMapRepageResponse) => {
    if (res.customerList != null) {
      setCustomerList(res.customerList);
      setCustomerListCount(res.customerListCount ?? res.customerList.length);
    }
    if (res.customerAreaList != null) {
      setAreaList(res.customerAreaList);
    }
    // 予約されていれば、新データと同じレンダーでリセットを確定（クエリ検索時）
    if (pendingMapResetRef.current) {
      pendingMapResetRef.current = false;
      setMapResetToken((t) => t + 1);
    }
  }, []);

  // 共通: repage 実行
  const runRepage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerMapAPI.repage(buildRepageParam());
      if (isOk(res)) {
        applyRepageResult(res);
      } else {
        pendingMapResetRef.current = false; // 失敗時は予約を解除（次のrepageへ漏らさない）
      }
      return res;
    } finally {
      setLoading(false);
    }
  }, [buildRepageParam, applyRepageResult]);

  /**
   * 初期化（参考: execShowModal → initAfterProc）。
   * 選択顧客・地図設定・マスター類を取得して状態にセットする。
   */
  const loadInit = useCallback(async (customerno?: number | null) => {
    setLoading(true);
    setError(null);
    // 新規オープンなので記憶していた表示位置は破棄（復元表示でのみ使う）。
    viewRef.current = null;
    // 参考: execShowModal が icons/makers をクリアしてから init する挙動の再現。
    // 実際のリセット確定は init データ反映時（下記）に行う。
    pendingMapResetRef.current = true;
    try {
      const res: CustomerMapInitResponse = await customerMapAPI.init(customerno);
      // 地図が出ないときの切り分け用：/api2/map/init の生レスポンスを出力する。
      // googleMapsApi 等が最上位に無い（例: customerdata 等の封筒で包まれている）場合、
      // 項目名のマッピングを修正する必要がある。
      console.log('[CustomerMap] /api2/map/init レスポンス:', res);
      if (!isOk(res)) {
        setError((res as any)?.message || '地図情報の取得に失敗しました');
        pendingMapResetRef.current = false; // 失敗時は予約を解除（次のrepageへ漏らさない）
        return res;
      }

      // 地図設定
      setConfig({
        googleMapsApi: res.googleMapsApi,
        googleMapsApiKey: res.googleMapsApiKey,
        defaultLat: res.googleMapsDefaultLat,
        defaultLng: res.googleMapsDefaultLng,
        defaultZoomNone: res.googleMapsDefaultZoomNone,
        defaultZoom: res.googleMapsDefaultZoomSelect,
        minZoom: res.googleMapsApiZoomMinLimit,
      });

      // マスター類
      setRankList(res.rankList ?? []);
      rankListRef.current = res.rankList ?? [];
      setQueryList(res.queryList ?? []);
      setQuerySearchTypeList(res.querySearchTypeList ?? {});
      setListViewCountMap(res.listViewCountMap ?? []);

      // 絞り込み初期値
      setQuerySearching(false);
      setPage(res.listPage ?? 1);
      setListViewCount(res.listViewCount ?? 0);
      setSortKey(res.sortKey ?? 0);
      setSortAsc(res.sortOrder ?? false);
      setSelectedQueryNo(null);
      filtersRef.current = {
        page: res.listPage ?? 1,
        listViewCount: res.listViewCount ?? 0,
        sortKey: res.sortKey ?? 0,
        sortAsc: res.sortOrder ?? false,
        queryNo: null,
      };

      // 一覧・周辺顧客
      setCustomerList(res.customerList ?? []);
      setAreaList(res.customerAreaList ?? []);
      setCustomerListCount((res.customerList ?? []).length);

      // 予約されていれば、新データと同じレンダーでピン描画リセットを確定
      if (pendingMapResetRef.current) {
        pendingMapResetRef.current = false;
        setMapResetToken((t) => t + 1);
      }

      // 選択顧客（自顧客）
      if (res.customerno != null) {
        const self: CustomerMapSelected = {
          customerno: res.customerno,
          companyname: res.companyname,
          customername: res.customername,
          latitude: res.latitude,
          longitude: res.longitude,
          rankno: res.rankno,
          rankname: resolveRankName(res.rankno) ?? res.rankname,
          tel: res.tel,
          mobiletel: res.mobiletel,
          address: res.address,
        };
        setSelected(self);
        setHome(self); // 起点顧客（カルテの顧客）を固定保持
        selectedNoRef.current = res.customerno;
        // 履歴初期化（参考: initMap の履歴保存）
        prevListRef.current = [res.customerno];
        nextListRef.current = [];
        refreshHistoryState();
      } else {
        setSelected(null);
        setHome(null);
        selectedNoRef.current = null;
        prevListRef.current = [];
        nextListRef.current = [];
        refreshHistoryState();
      }
      return res;
    } finally {
      setLoading(false);
    }
  }, [resolveRankName, refreshHistoryState]);

  /**
   * 表示領域の変更で再検索（参考: initMap の bounds_changed → repage）。
   * 地図側から新しい境界を渡して呼ぶ。
   */
  const searchByBounds = useCallback(async (bounds: CustomerMapBounds) => {
    boundsRef.current = bounds;
    return runRepage();
  }, [runRepage]);

  /**
   * 登録済検索条件での検索実行（参考: selectQuery）。
   * ページ=1・ソート初期化してから repage。
   * @param queryNoArg 明示指定（未指定なら selectedQueryNo を使用）
   */
  const selectQuery = useCallback(async (queryNoArg?: number | null) => {
    const queryNo = queryNoArg !== undefined ? queryNoArg : selectedQueryNo;
    setSelectedQueryNo(queryNo);
    setQuerySearching(queryNo != null);
    setPage(1);
    setSortKey(0);
    setSortAsc(false);
    filtersRef.current = { ...filtersRef.current, page: 1, sortKey: 0, sortAsc: false, queryNo };
    // 参考: クエリ検索ボタンが icons/makers/area をクリアして描き直す挙動の再現。
    // リセット確定は repage データ反映時（applyRepageResult）に行う。
    pendingMapResetRef.current = true;
    return runRepage();
  }, [selectedQueryNo, runRepage]);

  /** ページ移動（参考: changeListPage）。 */
  const changePage = useCallback(async (pageNo: number) => {
    setPage(pageNo);
    filtersRef.current = { ...filtersRef.current, page: pageNo };
    return runRepage();
  }, [runRepage]);

  /** ソート切替（参考: sortList。同一キーは昇順/降順トグル、別キーは昇順）。 */
  const changeSort = useCallback(async (key: number) => {
    const f = filtersRef.current;
    let nextAsc: boolean;
    if (key === f.sortKey) {
      nextAsc = !f.sortAsc;
    } else {
      nextAsc = true;
    }
    setSortKey(key);
    setSortAsc(nextAsc);
    setPage(1);
    filtersRef.current = { ...f, sortKey: key, sortAsc: nextAsc, page: 1 };
    return runRepage();
  }, [runRepage]);

  /** 表示件数変更（参考: changeListCount）。 */
  const changeListCount = useCallback(async (count: number) => {
    setListViewCount(count);
    setPage(1);
    filtersRef.current = { ...filtersRef.current, listViewCount: count, page: 1 };
    return runRepage();
  }, [runRepage]);

  /** 選択履歴を保存（参考: saveHistory）。 */
  const saveHistory = useCallback((customerno: number) => {
    const prev = prevListRef.current;
    if (prev[prev.length - 1] !== customerno) {
      prev.push(customerno);
    }
    nextListRef.current = [];
    refreshHistoryState();
  }, [refreshHistoryState]);

  /** 選択履歴をリセット（参考: clearHistory）。 */
  const clearHistory = useCallback(() => {
    prevListRef.current = [];
    nextListRef.current = [];
    refreshHistoryState();
  }, [refreshHistoryState]);

  /**
   * 顧客選択（参考: selectInfo/selectInfoAfterProc/selectAfterProc）。
   * 顧客Noから詳細を取得して選択状態にセットする。
   * @param pushHistory 履歴へ積むか（既定: true）
   */
  const selectCustomer = useCallback(async (customerno: number, pushHistory = true) => {
    if (selectedNoRef.current === customerno) return null;
    setLoading(true);
    try {
      const res = await customerMapAPI.select(customerno);
      if (!isOk(res) || res.customerno == null) return res;

      const sel: CustomerMapSelected = {
        customerno: res.customerno,
        companyname: res.companyname,
        customername: res.customername,
        latitude: res.latitude,
        longitude: res.longitude,
        rankno: res.rankno,
        rankname: resolveRankName(res.rankno) ?? res.rankname,
        tel: res.tel,
        mobiletel: res.mobiletel,
        address: res.address,
      };
      setSelected(sel);
      selectedNoRef.current = res.customerno;
      if (pushHistory) saveHistory(res.customerno);
      return res;
    } finally {
      setLoading(false);
    }
  }, [resolveRankName, saveHistory]);

  /** 戻る（参考: 戻るボタン）。 */
  const goPrev = useCallback(async () => {
    const prev = prevListRef.current;
    if (prev.length <= 1) return null;
    const target = prev[prev.length - 2];
    nextListRef.current.push(prev.pop() as number);
    refreshHistoryState();
    return selectCustomer(target, false);
  }, [refreshHistoryState, selectCustomer]);

  /** 進む（参考: 進むボタン）。 */
  const goNext = useCallback(async () => {
    const next = nextListRef.current;
    if (next.length === 0) return null;
    const target = next.pop() as number;
    prevListRef.current.push(target);
    refreshHistoryState();
    return selectCustomer(target, false);
  }, [refreshHistoryState, selectCustomer]);

  return {
    // 状態
    loading,
    error,
    config,
    rankList,
    queryList,
    querySearchTypeList,
    listViewCountMap,
    customerList,
    customerListCount,
    areaList,
    page,
    listViewCount,
    sortKey,
    sortAsc,
    selectedQueryNo,
    querySearching,
    selected,
    home,
    canGoPrev,
    canGoNext,
    mapResetToken,
    // 絞り込み操作
    setSelectedQueryNo,
    // メソッド
    loadInit,
    searchByBounds,
    selectQuery,
    changePage,
    changeSort,
    changeListCount,
    selectCustomer,
    saveHistory,
    clearHistory,
    goPrev,
    goNext,
    // ユーティリティ
    resolveRankName,
    buildRepageParam,
    // 表示位置の記憶/取り出し（戻るボタンでの画角復元用）
    rememberView,
    peekView,
  };
}
